import React, { useEffect, useRef, useState } from "react";
import api from "utils/axios";
import Button from "components/ui/Button";
import { HiXMark } from "react-icons/hi2";
import Select from "react-select";
const SuburbsComponent = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuburbs, setSelectedSuburbs] = useState(value || []);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Fetch all suggestions once when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch suburbs
        const suburbResponse = await api.get("/admin/suburb");
        const suburbData = suburbResponse.data.data;
        setSuggestions(suburbData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Handle input change and filter suggestions
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.trim()) {
      const filtered = suggestions.filter(
        (item) =>
          item.suburb.toLowerCase().includes(value.toLowerCase()) ||
          item.postcode.includes(value)
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const match = suggestions.find(
        (item) => item.suburb.toLowerCase() === inputValue.toLowerCase()
      );

      if (
        match &&
        !selectedSuburbs.some((suburb) => suburb.suburb === match.suburb)
      ) {
        handleAddSuburb(match);
      }
    }
  };

  const handleAddSuburb = (suburbObj) => {
    if (!selectedSuburbs.some((suburb) => suburb.suburb === suburbObj.suburb)) {
      const newSuburb = {
        suburb: suburbObj.suburb,
        state: suburbObj.state,
        postcode: suburbObj.postcode,
      };

      const updatedSuburbs = [...selectedSuburbs, newSuburb];
      setSelectedSuburbs(updatedSuburbs);
      onChange(updatedSuburbs);
    }

    setInputValue("");
    setShowSuggestions(false);
  };

  const handleRemoveSuburb = (suburbToRemove) => {
    const updatedSuburbs = selectedSuburbs.filter(
      (suburb) => suburb.suburb !== suburbToRemove.suburb
    );
    setSelectedSuburbs(updatedSuburbs);
    onChange(updatedSuburbs);
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <div>
        <div className="flex gap-2 relative" ref={inputRef}>
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter suburb"
              className="form-input w-full border border-mediumgray"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto z-70">
                {filteredSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleAddSuburb(suggestion)}
                  >
                    {suggestion?.suburb} {suggestion?.state}{" "}
                    {suggestion?.postcode}
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              const match = suggestions.find(
                (item) => item.suburb.toLowerCase() === inputValue.toLowerCase()
              );
              if (match) handleAddSuburb(match);
            }}
            className="black-button"
            disabled={!inputValue.trim()}
          >
            Add
          </Button>
        </div>
      </div>

      {selectedSuburbs?.length >= 1 && (
        <div className="space-y-4 mt-4">
          <p className="text-sm font-medium text-darkergray">
            Selected Suburbs:
          </p>

          {selectedSuburbs.map((suburb, index) => (
            <div
              key={`${suburb.suburb}-${suburb.postcode}`}
              className="border border-mediumgray rounded-lg p-4 bg-lightgray"
            >
              {/* Suburb header with remove button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-darkergray">
                    {suburb?.suburb} {suburb?.state} {suburb?.postcode}
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveSuburb(suburb)}
                  className="text-darkergray"
                >
                  <HiXMark className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
const ContactAlertForm = ({
  alertData,
  onSubmit,
  onCancel,
  isLoading,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    name: alertData?.alertName || "",
    message: alertData?.message || "",
    triggerDate: alertData?.triggerDate?.split("T")[0] || "",
    expiryDate: alertData?.expiryDate?.split("T")[0] || "",
    recurring: alertData?.recurring || "none",
    filters: {
      group: alertData?.filters?.group || "",
      suburb: alertData?.filters?.suburb || [],
      cookies: alertData?.filters?.cookies || "",
      isDeveloper: alertData?.filters?.isDeveloper || false,
      smsSales: alertData?.filters?.smsSales || false,
      isCleaned: alertData?.filters?.isCleaned || false,
      pastPurchaser: alertData?.filters?.pastPurchaser || false,
      pastVendor: alertData?.filters?.pastVendor || false,
      hasWhatsapp: alertData?.filters?.hasWhatsapp || false,
      cookieActive: alertData?.filters?.cookieActive || false,
    },
  });

  const booleanFilters = [
    { key: "isDeveloper", label: "Developer" },
    { key: "smsSales", label: "SMS Sales" },
    { key: "isCleaned", label: "Cleaned" },
    { key: "pastPurchaser", label: "Past Purchaser" },
    { key: "pastVendor", label: "Past Vendor" },
    { key: "hasWhatsapp", label: "Has WhatsApp" },
    { key: "cookieActive", label: "Cookie Active" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [name]: value,
      },
    }));
  };

  const handleSuburbChange = (suburbs) => {
    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        suburb: suburbs,
      },
    }));
  };

  const handleBooleanFilterChange = (filterKey) => {
    setFormData((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterKey]: !prev.filters[filterKey],
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Filter out false boolean values and empty arrays
    const cleanFilters = Object.keys(formData.filters).reduce((acc, key) => {
      const value = formData.filters[key];

      // Include non-empty strings, non-empty arrays, and true booleans
      if (
        (typeof value === "string" && value.trim() !== "") ||
        (Array.isArray(value) && value.length > 0) ||
        (typeof value === "boolean" && value === true)
      ) {
        acc[key] = value;
      }

      return acc;
    }, {});

    if (isEdit) {
      onSubmit({
        alertName: alertData.alertName,
        updates: {
          message: formData.message,
          triggerDate: formData.triggerDate,
          expiryDate: formData.expiryDate,
          recurring: formData.recurring,
        },
        filters: {
          ...cleanFilters,
          suburb: formData.filters.suburb.map((suburb) => suburb.suburb),
        },
      });
    } else {
      onSubmit({
        ...formData,
        filters: {
          ...cleanFilters,
          suburb: formData.filters.suburb.map((suburb) => suburb.suburb),
        },
        
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="form-label">
          Alert Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          disabled={isEdit}
          className="form-input w-full p-2 border border-lightgray "
          placeholder="e.g., Christmas Hamper"
          required
        />
      </div>

      <div>
        <label className="form-label">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          className="form-textarea w-full p-2 border border-lightgray "
          rows="3"
          placeholder="Use [Name] to include contact name in message. Use [PropertyAssessmentLink] for property assessment link."
          required
        />
        <p className="text-xs text-gmediumgray mt-1">
          Tip: Use [Name] in your message to personalize it with contact names. For property assessment link use [PropertyAssessmentLink]
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="form-label">
            Trigger Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="triggerDate"
            value={formData.triggerDate}
            onChange={handleInputChange}
            className="form-input w-full p-2 border border-lightgray"
            required
          />
        </div>

        <div>
          <label className="form-label">Expiry Date <span className="text-red-500">*</span></label>
          <input
            type="date"
            name="expiryDate"
            value={formData.expiryDate}
            onChange={handleInputChange}
            className="form-input w-full p-2 border border-lightgray "
            required
          />
        </div>
      </div>

      <div>
        <label className="form-label">Recurring</label>
        <select
          name="recurring"
          value={formData.recurring}
          onChange={handleInputChange}
          className="form-input w-full p-2 border border-lightgray "
        >
          <option value="none">None</option>
          <option value="yearly">Yearly</option>
          <option value="monthly">Monthly</option>
          <option value="weekly">Weekly</option>
        </select>
      </div>
      {!isEdit && (
        <div className="border-t pt-4">
          <h6 className="font-medium mb-3">Target Filters</h6>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Group</label>
              <select
                name="group"
                value={formData.filters.group}
                onChange={(e) =>
                  handleFilterChange("group", e.target.value)
                }
                className="form-input w-full p-2 border border-lightgray "
              >
                <option value="">All Groups</option>
                <option value="Connectors">Connectors</option>
                <option value="Pipeline">Pipeline</option>
              </select>
            </div>

            <div>
              <label className="form-label">Suburb</label>
              <SuburbsComponent
                value={formData.filters.suburb || []}
                onChange={handleSuburbChange}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="form-label">Selling Timeline</label>
            <select
              name="cookies"
              value={formData.filters.cookies}
              onChange={(e) => handleFilterChange("cookies", e.target.value)}
              className="form-input w-full p-2 border border-lightgray "
            >
              <option value="">All Timelines</option>
              <option value="0-3months">0-3 months</option>
              <option value="3-6months">3-6 months</option>
              <option value="6-12months">6-12 months</option>
              <option value="12+months">12+ months</option>
            </select>
          </div>

          <div className="mt-6">
            <label className="form-label mb-3 block">Tags</label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {booleanFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => handleBooleanFilterChange(filter.key)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    formData.filters[filter.key]
                      ? "bg-black text-white border-black"
                      : "bg-white text-darkergray border-mediumgray hover:border-darkergray"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-mediumgray mt-2">
              Click to toggle filters. Selected filters will be applied as true
              conditions.
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" onClick={onCancel} className="gray-button">
          Cancel
        </Button>
        <Button
          type="submit"
          className="black-button"
          loading={isLoading}
          disabled={isLoading}
        >
          {isEdit ? "Update Alert" : "Create Alert"}
        </Button>
      </div>
    </form>
  );
};

export default ContactAlertForm;
